import { ArrayLikeUtils } from './ArrayLikeUtils';
import {
  buildReverseMapping,
  DataStoreValue,
  getLabelMarkIdsForPane,
  getInterpolationData,
  getMoveLabelMarkIdSet,
  interpolateValue,
  preserveArrayType,
} from './DataStorePaneStateUtility';
import { DataStorePaths } from './DataStorePaths';
import { PaneStateAttribute, PaneStateAttributeUtil } from './PaneStateAttribute';

export interface PaneInterpolationContext {
  getValue(path: string): DataStoreValue;
  setOverride(path: string, value: DataStoreValue): void;
  buildMarkIdIndexMapping(fromMarkIds: DataStoreValue, toMarkIds: DataStoreValue): Map<number, number>;
}

export interface PaneInterpolation {
  interpolatePane(
    context: PaneInterpolationContext,
    paneId: string,
    targetGetValue: (path: string) => DataStoreValue,
    fraction: number,
    exitingToIndices?: Set<number>
  ): void;
}

export function buildEnteringToIndices(toMarkIds: DataStoreValue, indexMapping: Map<number, number>): Set<number> {
  const mappedToIndices = new Set(indexMapping.values());
  const enteringToIndices = new Set<number>();
  const toArr = ArrayLikeUtils.toArray<number>(toMarkIds);
  for (let toIdx = 0; toIdx < toArr.length; toIdx++) {
    if (!mappedToIndices.has(toIdx)) {
      enteringToIndices.add(toIdx);
    }
  }
  return enteringToIndices;
}

export function interpolatePathWithMapping(
  context: PaneInterpolationContext,
  targetGetValue: (path: string) => DataStoreValue,
  path: string,
  attribute: PaneStateAttribute,
  fraction: number,
  indexMapping: Map<number, number>,
  enteringToIndices: Set<number> = new Set<number>(),
  exitingToIndices?: Set<number>
): void {
  const startValue = context.getValue(path);
  const endValue = targetGetValue(path);
  if (startValue === undefined || startValue === null || endValue === undefined || endValue === null) {
    return;
  }

  const startArray = ArrayLikeUtils.toArray<number>(startValue);
  const endArray = ArrayLikeUtils.toArray<number>(endValue);
  if (startArray.length > 0 && endArray.length > 0) {
    const interpolated = interpolateArray(
      startArray,
      endArray,
      attribute,
      indexMapping,
      (toIndex: number) => (enteringToIndices.has(toIndex) || exitingToIndices?.has(toIndex) ? fraction : fraction),
      enteringToIndices,
      exitingToIndices
    );
    if (interpolated.length > 0) {
      context.setOverride(path, preserveArrayType(endValue, interpolated));
    }
    return;
  }

  if (typeof startValue === 'number' && typeof endValue === 'number') {
    context.setOverride(path, interpolateValue(startValue, endValue, fraction, attribute));
  }
}

export function interpolatePathWithPerIndexFractions(
  context: PaneInterpolationContext,
  targetGetValue: (path: string) => DataStoreValue,
  path: string,
  attribute: PaneStateAttribute,
  indexMapping: Map<number, number>,
  fractionForToIndex: Map<number, number>,
  enteringToIndices: Set<number> = new Set<number>(),
  exitingToIndices?: Set<number>
): void {
  const startValue = context.getValue(path);
  const endValue = targetGetValue(path);
  if (startValue === undefined || startValue === null || endValue === undefined || endValue === null) {
    return;
  }

  const startArray = ArrayLikeUtils.toArray<number>(startValue);
  const endArray = ArrayLikeUtils.toArray<number>(endValue);
  if (startArray.length > 0 && endArray.length > 0) {
    const interpolated = interpolateArray(
      startArray,
      endArray,
      attribute,
      indexMapping,
      (toIndex: number) => fractionForToIndex.get(toIndex) ?? 1.0,
      enteringToIndices,
      exitingToIndices
    );
    if (interpolated.length > 0) {
      context.setOverride(path, preserveArrayType(endValue, interpolated));
    }
    return;
  }

  if (typeof startValue === 'number' && typeof endValue === 'number') {
    context.setOverride(path, interpolateValue(startValue, endValue, fractionForToIndex.get(0) ?? 1.0, attribute));
  }
}

export function interpolateLabelEncodings(
  context: PaneInterpolationContext,
  paneId: string,
  targetGetValue: (path: string) => DataStoreValue,
  getFractionForAttribute: (attribute: PaneStateAttribute) => number,
  moveLabelMarkIdSet?: Set<number>
): void {
  const getSourceValue = (path: string): DataStoreValue => context.getValue(path);
  const activeMoveLabelMarkIdSet =
    moveLabelMarkIdSet ??
    getMoveLabelMarkIdSet(
      paneId,
      (id: string) => getLabelMarkIdsForPane(getSourceValue, id),
      (id: string) => getLabelMarkIdsForPane(targetGetValue, id)
    );
  if (activeMoveLabelMarkIdSet.size === 0) {
    return;
  }

  const fromLabelMarkIds = getLabelMarkIdsForPane(getSourceValue, paneId);
  const toLabelMarkIds = getLabelMarkIdsForPane(targetGetValue, paneId);
  const indexMapping = context.buildMarkIdIndexMapping(fromLabelMarkIds, toLabelMarkIds);
  const toArr = ArrayLikeUtils.toArray<number>(toLabelMarkIds);

  for (const attribute of PaneStateAttributeUtil.getAllLabelAttributes()) {
    const path = DataStorePaths.getKeyPathForAttribute(paneId, attribute);
    if (!path) {
      continue;
    }
    const moveFraction = getFractionForAttribute(attribute);
    const fractionForToIndex = new Map<number, number>();
    for (let toIdx = 0; toIdx < toArr.length; toIdx++) {
      const markId = toArr[toIdx];
      fractionForToIndex.set(toIdx, activeMoveLabelMarkIdSet.has(markId) ? moveFraction : 1.0);
    }
    interpolatePathWithPerIndexFractions(context, targetGetValue, path, attribute, indexMapping, fractionForToIndex);
  }
}

export function fixPrevNextForPane(context: PaneInterpolationContext, paneId: string): void {
  const encodingsPath = `PDMarks.${paneId}.Encodings`;

  const markFlagsValue = context.getValue(`${encodingsPath}.mark_flags`);
  const xValue = context.getValue(`${encodingsPath}.x`);
  const yValue = context.getValue(`${encodingsPath}.y`);
  const xprevValue = context.getValue(`${encodingsPath}.x_prev`);
  const yprevValue = context.getValue(`${encodingsPath}.y_prev`);
  const xnextValue = context.getValue(`${encodingsPath}.x_next`);
  const ynextValue = context.getValue(`${encodingsPath}.y_next`);

  const markFlags = ArrayLikeUtils.toArray(markFlagsValue);
  const x = ArrayLikeUtils.toArray(xValue);
  const y = ArrayLikeUtils.toArray(yValue);
  const xprev = ArrayLikeUtils.toArray(xprevValue);
  const yprev = ArrayLikeUtils.toArray(yprevValue);
  const xnext = ArrayLikeUtils.toArray(xnextValue);
  const ynext = ArrayLikeUtils.toArray(ynextValue);

  if (
    markFlags.length === 0 ||
    x.length === 0 ||
    y.length === 0 ||
    xprev.length === 0 ||
    yprev.length === 0 ||
    xnext.length === 0 ||
    ynext.length === 0
  ) {
    return;
  }

  const MARK_FLAG_END = 1;
  const MARK_FLAG_END_PRIMITIVE = 2;
  const numMarks = Math.min(markFlags.length, x.length, y.length, xprev.length, yprev.length, xnext.length, ynext.length);

  let markIndexWithinStrip = 0;
  for (let i = 0; i < numMarks; i++) {
    if (markIndexWithinStrip === 0) {
      xprev[i] = x[i];
      yprev[i] = y[i];
    }

    const markFlag = markFlags[i];
    if ((markFlag & MARK_FLAG_END) !== 0 || (markFlag & MARK_FLAG_END_PRIMITIVE) !== 0) {
      xnext[i] = x[i];
      ynext[i] = y[i];
      markIndexWithinStrip = 0;
    } else {
      markIndexWithinStrip++;
    }
  }

  context.setOverride(`${encodingsPath}.x_prev`, preserveArrayType(xprevValue, xprev));
  context.setOverride(`${encodingsPath}.y_prev`, preserveArrayType(yprevValue, yprev));
  context.setOverride(`${encodingsPath}.x_next`, preserveArrayType(xnextValue, xnext));
  context.setOverride(`${encodingsPath}.y_next`, preserveArrayType(ynextValue, ynext));
}

function interpolateArray(
  startArray: number[],
  endArray: number[],
  attribute: PaneStateAttribute,
  indexMapping: Map<number, number>,
  getFractionForToIndex: (toIndex: number) => number,
  enteringToIndices: Set<number>,
  exitingToIndices?: Set<number>
): number[] {
  const reverseMapping = buildReverseMapping(indexMapping);
  return endArray.map((endVal, toIndex) => {
    const interpolationData = getInterpolationData(
      startArray,
      endVal,
      toIndex,
      attribute,
      reverseMapping,
      enteringToIndices,
      exitingToIndices
    );
    if (typeof interpolationData.startVal !== 'number' || typeof interpolationData.actualEndVal !== 'number') {
      return interpolationData.actualEndVal;
    }
    return interpolateValue(interpolationData.startVal, interpolationData.actualEndVal, getFractionForToIndex(toIndex), attribute);
  });
}
